import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Quote, ShieldCheck, Timer } from "lucide-react";
import { api } from "@/lib/api";
import { Dropzone } from "@/components/Dropzone";
import { FindingCard } from "@/components/FindingCard";

const STEPS = [
  ["Drop it in", "Upload a PDF or Word file. We read the text and propose a document type with a confidence score, which you can override."],
  ["Run the checklist", "Each type has its own list of things that tend to go wrong, from uncapped liability to arithmetic errors."],
  ["Act on the pack", "Get color-coded findings tied to quotes and page numbers, plus a plain-English memo ready to paste."],
];

const DEMO = {
  severity: "red", page: 2, title: "Confidentiality that never ends",
  quote: "The obligations of this Agreement shall survive in perpetuity.",
  why: "Perpetual obligations are unusual for general business information and hard to comply with forever.",
  action: "Propose a 2 to 3 year survival period, with trade secrets protected for as long as they remain secret.",
};

const Hero = () => {
  const nav = useNavigate();
  return (
    <section className="max-w-6xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-14 items-start">
      <div className="lg:col-span-6 rise">
        <p className="eyebrow mb-6">Document review, by type</p>
        <h1 className="font-serif-d text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight mb-8">
          Every document deserves its <em className="italic">own</em> checklist.
        </h1>
        <p className="text-lg text-[var(--ink-2)] leading-relaxed max-w-lg mb-10">
          PDF Review works out what you uploaded, then reviews it the way a specialist would. NDAs, vendor contracts, leases, invoices, resumes and insurance policies. Each finding comes with a quote and page number.
        </p>
        <ul className="flex flex-wrap gap-x-6 gap-y-3 text-sm text-[var(--ink-2)] mb-12">
          <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> Nothing gets sent or shared</li>
          <li className="flex items-center gap-2"><Timer className="w-4 h-4" strokeWidth={1.5} /> About a minute per review</li>
          <li className="flex items-center gap-2"><Quote className="w-4 h-4" strokeWidth={1.5} /> Every finding quotes the source</li>
        </ul>
        <dl className="grid grid-cols-3 border-t border-[var(--rule)]">
          {[["6", "Checklists"], ["~60s", "Per review"], ["p.#", "On every finding"]].map(([n, l]) => (
            <div key={l} className="pt-5 pr-4">
              <dt className="font-serif-d text-3xl">{n}</dt>
              <dd className="eyebrow mt-1">{l}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="lg:col-span-6 rise" style={{ animationDelay: "120ms" }}>
        <Dropzone onFile={(file) => nav("/new", { state: { file } })} />
        <div className="flex items-center justify-between mt-4">
          <p className="eyebrow">No account needed to try</p>
          <button onClick={() => nav("/new", { state: { sample: true } })} data-testid="hero-try-sample-btn"
            className="text-sm underline underline-offset-4 decoration-[var(--ink-3)] hover:decoration-[var(--ink)]">
            Or use a sample NDA
          </button>
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => (
  <section className="border-t border-[var(--rule)]">
    <div className="max-w-6xl mx-auto px-6 py-24">
      <p className="eyebrow mb-4">How it works</p>
      <h2 className="font-serif-d text-4xl md:text-5xl tracking-tight max-w-2xl mb-16">A focused review tool, not a general chatbot.</h2>
      <div className="grid md:grid-cols-3 gap-12">
        {STEPS.map(([t, d], i) => (
          <div key={t} className="border-t border-[var(--ink)] pt-6">
            <p className="font-mono-d text-xs text-[var(--ink-3)] mb-4">0{i + 1}</p>
            <h3 className="font-serif-d text-2xl mb-3">{t}</h3>
            <p className="text-sm text-[var(--ink-2)] leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const PlaybookGrid = ({ items }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-l border-t border-[var(--rule)]">
    {items.map((p) => (
      <Link key={p.slug} to={`/playbooks/${p.slug}`} data-testid={`playbook-card-${p.slug}`}
        className="group border-r border-b border-[var(--rule)] p-8 bg-[var(--card)] hover:bg-[var(--paper-2)] transition-colors duration-200">
        <div className="flex justify-between font-mono-d text-[11px] tracking-widest uppercase text-[var(--ink-3)] mb-10">
          <span>{p.number}</span><span>{p.category}</span>
        </div>
        <h3 className="font-serif-d text-3xl mb-3">{p.name}</h3>
        <p className="text-sm text-[var(--ink-2)] leading-relaxed mb-8">{p.audience}</p>
        <span className="font-mono-d text-[11px] tracking-widest uppercase inline-flex items-center gap-1.5">
          View checklist <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </Link>
    ))}
  </div>
);

const PlaybooksSection = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/playbooks").then((r) => setItems(r.data)); }, []);
  return (
    <section className="border-t border-[var(--rule)]">
      <div className="max-w-6xl mx-auto px-6 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="eyebrow mb-4">Use Cases</p>
            <h2 className="font-serif-d text-4xl md:text-5xl tracking-tight">Six specialist checklists.</h2>
          </div>
          <Link to="/playbooks" className="btn-line text-sm px-5 py-2.5 inline-flex items-center gap-2 self-start" data-testid="landing-all-playbooks-btn">
            See all use cases <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <PlaybookGrid items={items} />
      </div>
    </section>
  );
};

const Principles = () => (
  <section className="border-t border-[var(--rule)]">
    <div className="max-w-6xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-start">
      <div>
        <p className="eyebrow mb-4">Principles</p>
        <h2 className="font-serif-d text-4xl md:text-5xl tracking-tight mb-6">Quiet, careful and easy to check.</h2>
        <p className="text-[var(--ink-2)] leading-relaxed mb-8 max-w-lg">
          Every finding shows you the wording it is based on, so you can check it in seconds. You decide what to push back on and what to send.
        </p>
        <ul className="space-y-3 text-sm text-[var(--ink-2)]">
          {["Each finding has a verbatim quote and page reference.", "Memos and issue lists are ready to copy.", "Signed-in users keep a full review history.", "Your file stays private: nothing is emailed or shared."].map((t) => (
            <li key={t} className="flex gap-3"><span className="mt-2 w-1.5 h-1.5 bg-[var(--ink)] shrink-0" />{t}</li>
          ))}
        </ul>
      </div>
      <div>
        <p className="eyebrow mb-4">Sample finding</p>
        <FindingCard finding={DEMO} index={0} />
      </div>
    </div>
  </section>
);

const Cta = () => (
  <section className="max-w-6xl mx-auto px-6">
    <div className="bg-[var(--ink)] text-[var(--paper)] px-10 py-14 flex flex-col md:flex-row justify-between md:items-center gap-8">
      <div>
        <p className="font-mono-d text-[11px] tracking-[0.18em] uppercase text-[var(--ink-3)] mb-3">Ready when you are</p>
        <h2 className="font-serif-d text-4xl">Have a document waiting? Start here.</h2>
      </div>
      <Link to="/new" className="bg-[var(--paper)] text-[var(--ink)] px-7 py-3.5 text-sm font-medium hover:bg-white transition-colors" data-testid="cta-upload-btn">
        Start a review
      </Link>
    </div>
  </section>
);

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Hero />
      <HowItWorks />
      <PlaybooksSection />
      <Principles />
      <Cta />
    </div>
  );
}
